import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import { listSearchSchema } from '@/utils/listSearch';
import {
  DONATION_DECISION_STEP,
  DONATION_DETAIL_ORIGIN,
  REGION_REPORT_TAB,
} from '../constants/marketPromotion.constants';

export const budgetTotalsSchema = z.object({
  budgetAmount: z.number(),
  disburseAmount: z.number(),
});

export const mpAdjustmentRowSchema = z.object({
  promoItemId: z.coerce.number(),
  itemNo: z.string(),
  itemCode: z.string().nullable(),
  name: z.string(),
  distributedAmount: z.number(),
});

export const mpAdjustmentResponseSchema = z.object({
  data: z.array(mpAdjustmentRowSchema),
  meta: paginationMetaSchema,
});

const mpBudgetProjectSchema = z.object({
  promoItemId: z.number(),
  name: z.string(),
  budgetAmount: z.number(),
});

export const mpSummaryRowSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  userMobile: z.string().nullable(),
  designationName: z.string().nullable(),
  departmentName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  budgetAmount: z.number(),
  disburseAmount: z.number(),
  distributedAmount: z.number(),
  approvedAmount: z.number(),
  duesAmount: z.number(),
  inHandAmount: z.number(),
  budgetProjects: z.array(mpBudgetProjectSchema),
});

export const mpSummaryTotalsSchema = budgetTotalsSchema.extend({
  distributedAmount: z.number(),
  approvedAmount: z.number(),
  duesAmount: z.number(),
  inHandAmount: z.number(),
});

export const mpSummaryResponseSchema = z.object({
  data: z.array(mpSummaryRowSchema),
  meta: paginationMetaSchema.extend({ totals: mpSummaryTotalsSchema }),
});

export const mpRegionRowSchema = z.object({
  regionId: z.coerce.number(),
  regionName: z.string(),
  sideRegion: z.string().nullable(),
  rsmUserId: z.coerce.number(),
  rsmName: z.string(),
  smName: z.string().nullable(),
  distributedAmount: z.number(),
});

const mpRegionTotalsSchema = z.object({ distributedAmount: z.number() });

const mpRegionReportTotalsSchema = mpRegionTotalsSchema.extend({ donations: z.number() });

export const mpRegionReportSchema = z.object({
  items: z.array(mpRegionRowSchema),
  totals: mpRegionReportTotalsSchema,
});

export const mpRegionProjectRowSchema = z.object({
  promoItemId: z.coerce.number(),
  itemCode: z.string().nullable(),
  itemName: z.string(),
  distributedAmount: z.number(),
});

export const mpRegionProjectReportSchema = z.object({
  items: z.array(mpRegionProjectRowSchema),
  totals: mpRegionReportTotalsSchema,
});

export const mpRegionDonationRowSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  userMobile: z.string().nullable(),
  promoItemName: z.string(),
  instituteName: z.string(),
  instituteCode: z.number().nullable(),
  instituteEiin: z.string().nullable(),
  teacherName: z.string().nullable(),
  teacherMobile: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  divisionName: z.string().nullable(),
  dcrDate: z.string(),
  distributedAmount: z.number(),
});

export const mpRegionDonationResponseSchema = z.object({
  data: z.array(mpRegionDonationRowSchema),
  meta: paginationMetaSchema.extend({ totals: mpRegionTotalsSchema }),
});

export const mpApprovalRowSchema = z.object({
  detailId: z.coerce.number(),
  recordId: z.coerce.number(),
  dcrDate: z.string(),
  promoItemId: z.coerce.number(),
  promoItemNo: z.coerce.number().nullable(),
  itemName: z.string(),
  claimedAmount: z.number(),
  approvedAmount: z.number(),
  approveTypeNo: z.number(),
  teacherName: z.string().nullable(),
  teacherMobile: z.string().nullable(),
  teacherDesignation: z.string().nullable(),
  teacherSubject: z.string().nullable(),
  instituteId: z.coerce.number().nullable(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteEiin: z.string().nullable(),
  instituteThana: z.string().nullable(),
  instituteDistrict: z.string().nullable(),
  instituteDivision: z.string().nullable(),
  enteredById: z.coerce.number().nullable(),
  enteredByName: z.string().nullable(),
  enteredByMobile: z.string().nullable(),
  enteredByContact: z.string().nullable(),
  enteredByThana: z.string().nullable(),
  enteredByDistrict: z.string().nullable(),
  enteredByDivision: z.string().nullable(),
  enteredAt: z.string().nullable(),
  recordComments: z.string().nullable(),
  recommendedBy: z.string().nullable(),
  recommendedAt: z.string().nullable(),
  recommendedComments: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  approvalComments: z.string().nullable(),
  paymentType: z.string().nullable(),
  paymentPlace: z.string().nullable(),
  givenDate: z.string().nullable(),
  totalStudent: z.number().nullable(),
  approxSaleQty: z.number().nullable(),
  witness: z.string().nullable(),
  transactionRef: z.string().nullable(),
  sourceOfFund: z.coerce.number().nullable(),
  sourceName: z.string().nullable(),
  callStatus: z.number().nullable(),
  calledBy: z.string().nullable(),
  callTime: z.string().nullable(),
  teacherFeedback: z.number().nullable(),
  isMisVerified: z.boolean(),
  misComment: z.string().nullable(),
  hasAttachment: z.boolean(),
});

export const mpApprovalDetailSchema = mpApprovalRowSchema.extend({
  dslNo: z.number().nullable(),
  detailComment: z.string().nullable(),
});

export const misCallerSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
});

export const misCallerListSchema = z.array(misCallerSchema);

export type MisCaller = z.infer<typeof misCallerSchema>;

export const sourceInstituteSchema = z.object({
  instituteCode: z.coerce.number(),
  instituteName: z.string(),
  instituteEiin: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
});

export const sourceInstituteListSchema = z.array(sourceInstituteSchema);

export type SourceInstitute = z.infer<typeof sourceInstituteSchema>;

export const donationDetailSearchSchema = z.object({
  from: z.enum(DONATION_DETAIL_ORIGIN).catch(DONATION_DETAIL_ORIGIN.approvals),
  step: z.enum(DONATION_DECISION_STEP).optional().catch(undefined),
});

export const mpApprovalTotalsSchema = z.object({
  pending: z.number(),
  recommended: z.number(),
  approved: z.number(),
  rejected: z.number(),
  approvedAmount: z.number(),
});

export const mpApprovalResponseSchema = z.object({
  data: z.array(mpApprovalRowSchema),
  meta: paginationMetaSchema.extend({ totals: mpApprovalTotalsSchema }),
});

export type BudgetTotals = z.infer<typeof budgetTotalsSchema>;
export type MpSummaryTotals = z.infer<typeof mpSummaryTotalsSchema>;
export type MpAdjustmentRow = z.infer<typeof mpAdjustmentRowSchema>;
export type MpAdjustmentResponse = z.infer<typeof mpAdjustmentResponseSchema>;
export type MpSummaryRow = z.infer<typeof mpSummaryRowSchema>;
export type MpBudgetProject = z.infer<typeof mpBudgetProjectSchema>;
export type MpSummaryResponse = z.infer<typeof mpSummaryResponseSchema>;
export type MpRegionRow = z.infer<typeof mpRegionRowSchema>;
export type MpRegionReport = z.infer<typeof mpRegionReportSchema>;
export type MpRegionProjectRow = z.infer<typeof mpRegionProjectRowSchema>;
export type MpRegionProjectReport = z.infer<typeof mpRegionProjectReportSchema>;
export type MpRegionDonationRow = z.infer<typeof mpRegionDonationRowSchema>;
export type MpRegionDonationResponse = z.infer<typeof mpRegionDonationResponseSchema>;
export type MpApprovalRow = z.infer<typeof mpApprovalRowSchema>;
export type MpApprovalDetail = z.infer<typeof mpApprovalDetailSchema>;
export type MpApprovalResponse = z.infer<typeof mpApprovalResponseSchema>;

export const mpReportSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  userId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  promoItemId: z.coerce.number().optional(),
  search: z.string().optional(),
});

const numberList = z.preprocess(
  (value) => (value === undefined ? undefined : Array.isArray(value) ? value : [value]),
  z.array(z.coerce.number()),
);

export const mpRegionFilterSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  budgetHolderId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  promoItemIds: numberList.optional(),
  itemCode: z.string().optional(),
});

export const mpRegionSearchSchema = mpRegionFilterSearchSchema.extend({
  ...listSearchSchema.shape,
  tab: z.enum(REGION_REPORT_TAB).optional().catch(undefined),
});

export const mpRegionDonationsSearchSchema = mpRegionFilterSearchSchema.extend({
  ...listSearchSchema.shape,
  rsmUserId: z.coerce.number().optional(),
  projectId: z.coerce.number().optional(),
});

export const mpApprovalSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  enteredBy: z.coerce.number().optional(),
  instituteId: z.coerce.number().optional(),
  search: z.string().optional(),
  promoItemIds: numberList.optional(),
  approveTypeNos: numberList.optional(),
  callStatus: z.coerce.number().optional(),
  teacherFeedback: z.coerce.number().optional(),
  calledBy: z.coerce.number().optional(),
  callDateFrom: z.string().optional(),
  callDateTo: z.string().optional(),
  isMisVerified: z.boolean().optional(),
});

export type MpReportSearch = z.infer<typeof mpReportSearchSchema>;
export type MpRegionFilterSearch = z.infer<typeof mpRegionFilterSearchSchema>;
export type MpRegionDonationsSearch = z.infer<typeof mpRegionDonationsSearchSchema>;
export type MpRegionDonationParams = MpRegionFilterSearch & {
  rsmUserId?: number | undefined;
  search?: string | undefined;
};
export type MpApprovalSearch = z.infer<typeof mpApprovalSearchSchema>;

export interface RecommendDonationPayload {
  approvedAmount: number;
  paymentType: string;
  transactionRef?: string;
  approxSaleQty: number;
  paymentPlace: string;
  witness: string;
  totalStudent: number;
  givenDate?: string;
  recommendedComments: string;
}

export interface RejectRecommendationPayload {
  comment: string;
}

export interface DecideDonationPayload {
  approveTypeNo: number;
  approvalComments: string;
}

export interface AmendDonationPayload {
  approvedAmount: number;
  sourceOfFund?: number;
  paymentType?: string;
  transactionRef?: string;
  approxSaleQty?: number;
  paymentPlace?: string;
  witness?: string;
  givenDate?: string;
  totalStudent?: number;
  recommendedComments?: string;
  approvalComments?: string;
}

export interface VerifyMisPayload {
  callStatus?: number;
  teacherFeedback?: number;
  misComment?: string;
}
